/**
 * Centralized Authentication Manager
 * Handles all auth operations with proper error handling and state management
 */

import { useAuthStore } from "~/stores/auth";
import { useAuthModal } from "~/composables/auth/useAuthModal";
import { logger } from "~/utils/logger";

export const useAuthManager = () => {
  const authStore = useAuthStore();
  const config = useRuntimeConfig();

  const state = reactive({
    isLoading: false,
    error: null as string | null,
  });

  // Derive auth state directly from the store via computed refs to avoid
  // assigning a reactive Proxy into another reactive() object, which can
  // cause infinite Proxy trap recursion in iOS 26 / JSC and overflow the
  // call stack before Vue's cycle detection fires.
  const isAuthenticated = computed(() => !!authStore.user);
  const user = computed(() => authStore.user);

  /**
   * Initialize authentication state
   * Called once during app startup
   */
  const initialize = async (): Promise<void> => {
    if (process.server) return;

    state.isLoading = true;
    state.error = null;

    try {
      // Check if SSR provided a user
      const ssrUser = useState("authUser").value;
      if (ssrUser) {
        authStore.user = ssrUser as any;
        authStore.sessionRestored = true;
        state.isLoading = false;
        return;
      }

      // Attempt session restoration
      await restoreSession();
    } catch (error) {
      state.error = "Failed to initialize authentication";
    } finally {
      state.isLoading = false;
    }
  };

  /**
   * Restore session from httpOnly cookies
   * Does NOT throw errors, handles them gracefully
   */
  const restoreSession = async (): Promise<void> => {
    if (!config.public.useBackend) {
      authStore.loadFromStorage();
      authStore.sessionRestored = true;
      return;
    }

    try {
      // Fetch user data (Kratos session is cookie-based, no refresh needed)
      await fetchCurrentUser();

      authStore.sessionRestored = true;
    } catch (error) {
      // Session restoration failed - user needs to login
      authStore.user = null;
      (authStore as any).token = null;
      authStore.sessionRestored = true; // Mark as attempted
    }
  };

  /**
   * Refresh authentication token
   * Note: With Kratos, sessions are cookie-based and refreshed automatically
   * This method is kept for backward compatibility but does nothing
   */
  const refreshToken = async (): Promise<void> => {
    // No-op: Kratos handles session refresh via cookies
    return;
  };

  /**
   * Fetch current user data
   */
  const fetchCurrentUser = async (): Promise<void> => {
    const base = config.public.apiBase.replace(/\/$/, "");

    const response: any = await $fetch(`${base}/users/me`, {
      method: "GET",
      credentials: "include",
    });

    authStore.user = response.user || response;
    authStore.saveToStorage();
  };

  /**
   * Ensure session is restored before proceeding
   * Used by middleware and protected operations
   * Returns true if authenticated AND verified
   * Throws 'VERIFICATION_REDIRECT' if user needs verification (don't catch, let middleware handle)
   * Returns false for other auth failures
   */
  const ensureAuthenticated = async (): Promise<boolean> => {
    logger.log("[ensureAuthenticated] Starting...");
    logger.log("[ensureAuthenticated] authStore.user:", !!authStore.user);
    logger.log(
      "[ensureAuthenticated] authStore.sessionRestored:",
      authStore.sessionRestored,
    );
    logger.log(
      "[ensureAuthenticated] authStore.verificationRequired:",
      authStore.verificationRequired,
    );

    // If user is already set in store (e.g., after registration/login), return true immediately
    if (authStore.user) {
      logger.log("[ensureAuthenticated] User already set, returning true");
      return true;
    }

    if (!authStore.sessionRestored) {
      logger.log(
        "[ensureAuthenticated] Session not restored, calling checkKratosSession...",
      );
      await authStore.checkKratosSession(false); // Don't throw, handle internally
      logger.log(
        "[ensureAuthenticated] After checkKratosSession, verificationRequired:",
        authStore.verificationRequired,
      );
    }

    // Check if verification is required (flag is set by checkKratosSession)
    if (authStore.verificationRequired) {
      logger.log(
        "[ensureAuthenticated] Verification required! Throwing VERIFICATION_REDIRECT",
      );
      throw new Error("VERIFICATION_REDIRECT");
    }

    // Check store directly for most up-to-date state
    const result = !!authStore.user;
    logger.log("[ensureAuthenticated] Returning:", result);
    return result;
  };

  /**
   * Handle authentication errors and token refresh
   * Note: With Kratos cookie-based auth, 401 means session expired - just logout
   */
  const handleAuthError = async (
    error: any,
    originalRequest: Function,
  ): Promise<any> => {
    const status =
      error?.status || error?.statusCode || error?.response?.status;

    if (status === 401 && authStore.sessionRestored) {
      // Session expired - check if we're on a booking page
      const router = useRouter();
      const route = router?.currentRoute?.value;
      const isBookingPage = route?.path?.startsWith("/booking");

      if (isBookingPage) {
        // Open auth modal instead of navigating away
        const { openAuthModal } = useAuthModal();
        await logout();
        openAuthModal("signin", route?.fullPath);
      } else {
        // For other pages, redirect to signin
        await logout();
        await navigateTo("/login");
      }
      throw error;
    }

    throw error;
  };

  /**
   * Sign in user
   */
  const signIn = async (credentials: any): Promise<void> => {
    state.isLoading = true;
    state.error = null;

    try {
      await authStore.login(credentials);
      await fetchCurrentUser();
    } catch (error: any) {
      state.error = error.message || "Sign in failed";
      throw error;
    } finally {
      state.isLoading = false;
    }
  };

  /**
   * Sign out user
   */
  const logout = async (): Promise<void> => {
    try {
      await authStore.logout();
    } catch (error) {}

    authStore.user = null;
    (authStore as any).token = null;
    authStore.sessionRestored = false;
  };

  return {
    state: readonly(state),
    isAuthenticated,
    user,
    initialize,
    restoreSession,
    ensureAuthenticated,
    handleAuthError,
    signIn,
    logout,
    refreshToken,
    fetchCurrentUser,
  };
};
